/**
 * Gateway hooks: the connection/health status (poll or SSE-driven). The SSE
 * stream pushes status straight into the same cache key the poll query reads,
 * so the two stay coherent.
 */

import {
  type GatewayEventsSource,
  hubQueryKeys,
  startGatewayEventsSse,
} from "@lifter/hub-ui/browser-shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useHubApi } from "./context";

interface GatewayStatusQueryOptions {
  polling?: boolean;
}

export function useGatewayStatusQuery(options: GatewayStatusQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.gatewayStatus(),
    queryFn: () => api.gateway.status(),
    refetchInterval: options.polling === false ? false : 10_000,
  });
}

export function useGatewayEventsSse(options: { enabled?: boolean } = {}) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<"connecting" | "connected" | "failed">("connecting");

  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") {
      setState("failed");
      return;
    }

    return startGatewayEventsSse({
      eventsUrl: api.gateway.eventsUrl(),
      createEventSource: (url, init) =>
        new EventSource(url, init) as unknown as GatewayEventsSource,
      onState: setState,
      onStatus: (status) => queryClient.setQueryData(hubQueryKeys.gatewayStatus(), status),
    });
  }, [api, enabled, queryClient]);

  return { connected: state === "connected", failed: state === "failed", state };
}
